import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useFieldArray } from 'react-hook-form'
import { isEmpty } from 'utils/object'

import { INTERVIEW_STEPS } from 'ducks/router'
import { useControlledForm } from 'hooks'
import { mapInternalStateToPayload } from '../actions'
import {
  ASSESSMENT_GROUP_FIELD,
  getDefaultValues,
  getQuestionComments,
  getValues,
} from '../model'
import { getSchema } from '../schema'

// NOTE: hook that exposes assessment form state and its handlers
// for other local hooks and interview components
export const useAssessmentFormState = (state, handlers) => {
  const { evaluation } = state
  const {
    handleChangeStep,
    handleFetch,
    handlePatchEvaluation,
    handleSetResponse,
  } = handlers

  const { evaluation_data, id } = evaluation ?? {}

  const defaultValues = getDefaultValues()

  const values = getValues({ assessments: evaluation_data })

  const initialQuestionComments = getQuestionComments({
    assessments: evaluation_data,
  })
  const [questionComments, setQuestionComments] = React.useState(
    () => initialQuestionComments,
  )
  React.useEffect(() => {
    setQuestionComments(getQuestionComments({ assessments: evaluation_data }))
  }, [evaluation_data])

  const handleQuestionCommentChange = React.useCallback(
    (sectionKey, questionId, value) => {
      setQuestionComments(prev => ({
        ...prev,
        [sectionKey]: {
          ...(prev?.[sectionKey] ?? {}),
          [String(questionId)]: value,
        },
      }))
    },
    [],
  )

  const schema = getSchema()

  const handleFormData = React.useCallback(
    async data => {
      const payload = mapInternalStateToPayload({
        ...data,
        questionComments,
      })

      const { error, response } = await handlePatchEvaluation({
        evaluationData: payload,
        id,
        status: 4,
      })

      if (response) {
        const { response } = await handleFetch()

        handleSetResponse(response)
        handleChangeStep(INTERVIEW_STEPS.REPORT)
      } else {
        console.log({ error })
      }
    },
    [
      id,
      handleChangeStep,
      handleFetch,
      handlePatchEvaluation,
      handleSetResponse,
      questionComments,
    ],
  )

  const { control, formState, handleFormSubmit, watch } = useControlledForm({
    defaultValues,
    values,
    resolver: yupResolver(schema),
    handleFormData,
  })

  const { fields } = useFieldArray({ control, name: ASSESSMENT_GROUP_FIELD })

  const { errors: { evaluators = [] } = {} } = formState

  const hasEmptyScore = (evaluators as { score?: unknown }[]).some(
    ({ score }: { score?: unknown } = {}) => !isEmpty(score),
  )

  return {
    state: {
      assessment: { control, fields, formState },
      hasEmptyScore,
      questionComments,
    },
    handlers: {
      handleFormSubmit,
      handleQuestionCommentChange,
      watch,
    },
  }
}
